/**
 * Centralized API Configuration
 * 
 * This file manages all API endpoints and base URL configuration.
 * 
 * IMPORTANT: Runtime detection always takes priority over build-time environment variables!
 * 
 * Production URL: https://techworth-backend.onrender.com
 */

import axios from 'axios';

// Production backend URL
const PRODUCTION_BACKEND_URL = 'https://techworth-backend.onrender.com';
const DEVELOPMENT_BACKEND_URL = 'http://localhost:5000';

// Check if we're on localhost at runtime (check this FIRST to avoid false positives)
const isLocalhost = () => {
  if (typeof window === 'undefined') return false;
  
  const hostname = window.location.hostname.toLowerCase();
  // Explicitly check for localhost variants first
  return (
    hostname === 'localhost' || 
    hostname === '127.0.0.1' ||
    hostname === '::1' ||
    hostname.startsWith('localhost') ||
    hostname.startsWith('127.0.0.1')
  );
};

// Check if we're on a production domain at runtime
// This function is called every time we need the API URL
// IMPORTANT: Check localhost first to avoid false positives
const isProductionDomain = () => {
  if (typeof window === 'undefined') return false;
  
  // If it's localhost, it's NOT production
  if (isLocalhost()) return false;
  
  const hostname = window.location.hostname.toLowerCase();
  const protocol = window.location.protocol;
  
  // If we're on the production domain, ALWAYS use production backend
  return (
    hostname === 'www.techworth.org.in' || 
    hostname === 'techworth.org.in' ||
    hostname.endsWith('.vercel.app') ||
    hostname.endsWith('.netlify.app') ||
    hostname.endsWith('.onrender.com') ||
    // Only consider HTTPS as production if it's NOT localhost (already checked above)
    (protocol === 'https:' && hostname !== 'localhost' && hostname !== '127.0.0.1')
  );
};

// Cache the API base URL to avoid excessive logging
let cachedApiBaseUrl = null;
let lastHostname = null;

// Get API base URL - DYNAMIC function that's called at runtime
// RUNTIME DETECTION ALWAYS TAKES PRIORITY over build-time env variables
export const getApiBaseUrl = () => {
  // Only recalculate if hostname changed (e.g., page navigation)
  const currentHostname = typeof window !== 'undefined' ? window.location.hostname : null;
  if (cachedApiBaseUrl && lastHostname === currentHostname) {
    return cachedApiBaseUrl;
  }
  
  lastHostname = currentHostname;
  
  // PRIORITY 1: Runtime detection - FORCE production URL if on production domain
  // This takes priority even if REACT_APP_API_URL is set to localhost
  if (isProductionDomain()) {
    cachedApiBaseUrl = PRODUCTION_BACKEND_URL;
    return cachedApiBaseUrl;
  }
  
  // PRIORITY 2: Runtime detection - Use localhost if on localhost
  if (isLocalhost()) {
    cachedApiBaseUrl = DEVELOPMENT_BACKEND_URL;
    return cachedApiBaseUrl;
  }
  
  // PRIORITY 3: Environment variable (only if runtime detection didn't match)
  // BUT: If env var points to localhost and we're on HTTPS, ignore it
  if (process.env.REACT_APP_API_URL) {
    const envUrl = process.env.REACT_APP_API_URL.trim();
    const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
    
    // If env var is localhost but we're on HTTPS, ignore it and use production
    if (envUrl.includes('localhost') && isHttps) {
      cachedApiBaseUrl = PRODUCTION_BACKEND_URL;
      return cachedApiBaseUrl;
    }
    
    cachedApiBaseUrl = envUrl;
    return cachedApiBaseUrl;
  }
  
  // PRIORITY 4: Fallback to production backend (safe default)
  cachedApiBaseUrl = PRODUCTION_BACKEND_URL;
  return cachedApiBaseUrl;
};

// Calculate API base URL once at module load for logging
const initialApiBaseUrl = getApiBaseUrl();

// Log configuration for debugging (only once at module load)
if (typeof window !== 'undefined') {
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('🔌 API Configuration (Runtime Detection)');
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('   Current URL:', window.location.href);
  console.log('   Hostname:', window.location.hostname);
  console.log('   Protocol:', window.location.protocol);
  console.log('   Is Production Domain:', isProductionDomain());
  console.log('   Is Localhost:', isLocalhost());
  console.log('   Initial Base URL:', initialApiBaseUrl);
  console.log('   REACT_APP_API_URL:', process.env.REACT_APP_API_URL || 'NOT SET');
  
  // Log warnings only if there's a real mismatch (shouldn't happen with fixed logic)
  if (initialApiBaseUrl.includes('localhost') && isProductionDomain()) {
    // This should not happen, but log it for debugging
    console.warn('   ⚠️ Note: Initial URL calculation may differ from runtime - this is normal');
    console.warn('   ✅ All API calls use getApiBaseUrl() which correctly detects the environment at runtime');
  }
  
  if (initialApiBaseUrl === PRODUCTION_BACKEND_URL) {
    console.log('   ✅ Using PRODUCTION backend:', PRODUCTION_BACKEND_URL);
  } else if (initialApiBaseUrl === DEVELOPMENT_BACKEND_URL) {
    console.log('   ✅ Using DEVELOPMENT backend:', DEVELOPMENT_BACKEND_URL);
  } else {
    console.log('   ✅ Using CUSTOM backend:', initialApiBaseUrl);
  }
  
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
}

// API Endpoints - DYNAMIC object that recalculates URLs on each access
// This ensures the URL is always correct at runtime, even if env vars are wrong
export const API_ENDPOINTS = {
  // Public endpoints
  get JOBS() { 
    const url = `${getApiBaseUrl()}/api/jobs`;
    return url;
  },
  get APPLICATIONS() { 
    const url = `${getApiBaseUrl()}/api/applications`;
    return url;
  },
  get CONTACT() { 
    const url = `${getApiBaseUrl()}/api/contact`;
    return url;
  },
  get NEWSLETTER() { 
    const url = `${getApiBaseUrl()}/api/newsletter`;
    return url;
  },
  get SERVICES() { 
    const url = `${getApiBaseUrl()}/api/services`;
    return url;
  },
  
  // Admin endpoints
  ADMIN: {
    get AUTH() {
      return {
        get LOGIN() { 
          const url = `${getApiBaseUrl()}/api/admin/auth/login`;
          return url;
        },
        get LOGOUT() { 
          const url = `${getApiBaseUrl()}/api/admin/auth/logout`;
          return url;
        },
        get ME() { 
          const url = `${getApiBaseUrl()}/api/admin/auth/me`;
          return url;
        },
      };
    },
    get DASHBOARD() {
      return {
        get STATS() { 
          const url = `${getApiBaseUrl()}/api/admin/dashboard/stats`;
          return url;
        },
        get RECENT_ACTIVITY() { 
          const url = `${getApiBaseUrl()}/api/admin/dashboard/recent-activity`;
          return url;
        },
      };
    },
    get JOBS() { 
      const url = `${getApiBaseUrl()}/api/admin/jobs`;
      return url;
    },
    get APPLICATIONS() { 
      const url = `${getApiBaseUrl()}/api/admin/applications`;
      return url;
    },
    get CONTACTS() { 
      const url = `${getApiBaseUrl()}/api/admin/contacts`;
      return url;
    },
    get NEWSLETTER() { 
      const url = `${getApiBaseUrl()}/api/admin/newsletter`;
      return url;
    },
    get SETTINGS() {
      return {
        get EMAIL_TEMPLATES() { 
          const url = `${getApiBaseUrl()}/api/admin/settings/email-templates`;
          return url;
        },
        get ACTIVITY_LOGS() { 
          const url = `${getApiBaseUrl()}/api/admin/settings/activity-logs`;
          return url;
        },
      };
    },
  },
};

// Default axios instance for all API requests
// This instance automatically includes withCredentials and handles 401 errors
// Note: Render.com free tier services can take 30-60 seconds to wake up from sleep
export const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true,
  timeout: 60000, // 60 second timeout (Render free tier needs time to wake up)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Update base URL when it changes (for runtime detection)
let currentBaseURL = getApiBaseUrl();
apiClient.defaults.baseURL = currentBaseURL;

// Interceptor to update base URL dynamically
apiClient.interceptors.request.use(
  (config) => {
    const newBaseURL = getApiBaseUrl();
    if (newBaseURL !== currentBaseURL) {
      currentBaseURL = newBaseURL;
      apiClient.defaults.baseURL = newBaseURL;
    }
    config.baseURL = newBaseURL;
    // Ensure withCredentials is always true
    config.withCredentials = true;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 errors
// Use a flag to prevent multiple redirects
let isRedirecting = false;
let last401Path = null;
let redirectTimeout = null;

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear any stale auth state
      if (typeof window !== 'undefined' && !isRedirecting) {
        const currentPath = window.location.pathname;
        const isAdminPage = currentPath.startsWith('/admin');
        const isLoginPage = currentPath === '/admin/login' || currentPath === '/admin/login/';
        
        // Only redirect if we're on an admin page (but not the login page)
        // This prevents redirect loops when checkAuth fails on the login page
        // Also prevent multiple redirects to the same path
        if (isAdminPage && !isLoginPage && last401Path !== currentPath) {
          // Clear any existing timeout
          if (redirectTimeout) {
            clearTimeout(redirectTimeout);
          }
          
          isRedirecting = true;
          last401Path = currentPath;
          
          // Store the current path to redirect back after login
          sessionStorage.setItem('redirectAfterLogin', currentPath);
          
          // Redirect immediately to prevent multiple API calls
          // Use window.location.href for immediate redirect (no async delay)
          window.location.href = '/admin/login';
          
          // Reset flag after a short delay to allow redirect to complete
          redirectTimeout = setTimeout(() => {
            isRedirecting = false;
          }, 1000);
        } else if (isLoginPage) {
          // On login page, 401 is expected - don't redirect or log
          // Just silently handle it
        }
      }
      
      // Suppress 401 errors in console when redirecting (they're expected)
      // Only log if we're not redirecting (e.g., on login page)
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname;
        const isLoginPage = currentPath === '/admin/login' || currentPath === '/admin/login/';
        if (!isLoginPage && isRedirecting) {
          // Suppress the error - we're already redirecting
          return Promise.reject(error);
        }
      }
    }
    return Promise.reject(error);
  }
);

// Create axios instance factory - creates a new instance with current base URL
export const createAxiosInstance = () => {
  return axios.create({
    baseURL: getApiBaseUrl(),
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

// Get axios config (for use with axios directly, not an instance)
export const getAxiosConfig = () => ({
  baseURL: getApiBaseUrl(),
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Legacy axiosConfig - NOTE: This uses the URL at module load time
// For dynamic URLs, use API_ENDPOINTS directly or createAxiosInstance()
export const axiosConfig = getAxiosConfig();

// Helper function to get full URL (always uses current runtime URL)
export const getApiUrl = (endpoint) => {
  const baseUrl = getApiBaseUrl();
  
  if (endpoint.startsWith('http')) {
    return endpoint;
  }
  return `${baseUrl}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
};

// Export base URL getter for direct use if needed
export default getApiBaseUrl;
